import React, { useRef, useState } from 'react'
import { StyledButtonPrimary } from '../../styles/ButtonStyles'
import { FormControl, Popover, PopoverActions } from '@mui/material'
import { useEditTechnologyMutation, useGetTechnologyTypesQuery } from '../../store/redux/reducers/technologiesSlice'
import { useForm, FieldValues } from 'react-hook-form'
import ImageUploadButton from '../Button/ImageUploadButton'
import { NewTechnologyFormValues, Technology } from '../../store/types'
import SelectTechnologyType from '../Form/SelectTechnologyType'
import { useNotification } from '../../store/redux/hooks'
import { Trans, useTranslation } from 'react-i18next'
import { createErrorMessage, formatTechnologyType, readFileAsync, stopPropagationForKey } from '../../store/utils'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import { Check } from '@mui/icons-material'
import TextInput from '../Form/Inputs/TextInput'
import { BaseDialogProps } from '../Dialog/BaseDialogProps'

type EditTechnologyPopupProps = BaseDialogProps & {
  technology: Technology
  anchorEl: HTMLLIElement | null
}

const EditTechnologyPopup = ({ technology, open, onClose, anchorEl }: EditTechnologyPopupProps) => {
  // RESET IMAGE
  const [clearImage, setClearImage] = useState(
    technology.logo !== '' && technology.logo !== null && technology.logo !== 'null'
  )

  // CONFIRMATION DIALOG
  const [openDialog, setOpenDialog] = useState(false)

  // TRANSLATION
  const { t } = useTranslation()

  // NOTIFICATIONS
  const { displayNotification } = useNotification()

  // TECHNOLOGIES' TYPES
  const { data: technologyTypes } = useGetTechnologyTypesQuery({})

  // FORM
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    formState: { isSubmitSuccessful, isSubmitting },
  } = useForm<NewTechnologyFormValues>({
    defaultValues: {
      name: technology.name,
      type: { value: technology.type, label: formatTechnologyType(technology.type) },
      logo: technology.logo,
    },
  })

  const [editTechnology] = useEditTechnologyMutation()

  const onSubmit = (data: FieldValues) => {
    if (data.logo === null || data.logo === undefined || data.logo === 'null') {
      data.logo = ''
    }
    readFileAsync(data.logo, async (result, type) => {
      try {
        const technologyResponse = await editTechnology({
          id: technology.id,
          technology: {
            name: data.name,
            logo: clearImage ? technology.logo : result === '' ? result : btoa(result as string),
            type: data.type.value,
            extension: calcExtension(type),
          },
        }).unwrap()
        reset(data)
        setClearImage(!!technologyResponse.logo && technologyResponse.logo !== 'null')
        displayNotification({ message: t('page.adminsPage.alerts.technology.edit.success'), type: 'success' })
        onClose?.()
      } catch (error) {
        reset(data, { keepDefaultValues: true })
        displayNotification({
          message: createErrorMessage(error),
          type: 'error',
        })
      }
    })
  }

  const calcExtension = (type: string | undefined) => {
    return clearImage ? technology.extension : type !== undefined ? type.split('/')[1].toUpperCase() : undefined
  }

  // CONFIRM
  const handleConfirm = () => {
    handleSubmit(onSubmit)()
    setOpenDialog(false)
  }

  const ref = useRef<PopoverActions>(null)

  const handleClose = () => {
    onClose?.()
    reset()
    setClearImage(!!technology.logo && technology.logo !== 'null')
  }

  return (
    <Popover
      action={ref}
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <FormControl
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
          p: 2,
        }}
        onKeyDown={e => stopPropagationForKey(e, 'Tab')}
      >
        <ImageUploadButton
          name="logo"
          label={t('page.adminsPage.form.logo')}
          register={register}
          watch={watch}
          resetField={resetField}
          clearImage={clearImage}
          setClearImage={setClearImage}
          popoverRef={ref}
        />

        <TextInput
          control={control}
          name={'name'}
          required={true}
          label={t('page.adminsPage.form.name')}
          rules={{ required: t('page.adminsPage.form.nameRequired') }}
        />

        {/* TECHNOLOGY TYPE */}
        <SelectTechnologyType control={control} options={technologyTypes} required={true} />

        {/* SAVE BUTTON */}
        <StyledButtonPrimary
          variant="contained"
          fullWidth
          onClick={() => setOpenDialog(true)}
          startIcon={<Check sx={{ width: 14, height: 14 }} />}
        >
          {t('page.adminsPage.edit.save')}
        </StyledButtonPrimary>

        <ConfirmationDialog
          open={openDialog}
          setOpen={setOpenDialog}
          handleConfirm={handleConfirm}
          title={t('page.adminsPage.dialog.edit.title')}
          content={<Trans i18nKey="page.adminsPage.dialog.edit.content" values={{ name: technology.name }} />}
          loading={isSubmitting || isSubmitSuccessful}
        />
      </FormControl>
    </Popover>
  )
}

export default EditTechnologyPopup